import {
  forwardRef,
  memo,
  useCallback,
  useDeferredValue,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react'

import { type Virtualizer, useVirtualizer } from '@tanstack/react-virtual'
import { Document, Page as ReactPdfPage, pdfjs } from 'react-pdf'

import 'react-pdf/dist/esm/Page/AnnotationLayer.css'
import 'react-pdf/dist/esm/Page/TextLayer.css'
import { twMerge } from 'tailwind-merge'

import {
  useIdleOverscanCanvas,
  useIdlePageLayers,
  usePdfScrollObserver,
  useReaderPresence,
  useSettledPageScale,
} from '@/components/common/PdfViewerV2/hooks'
import {
  type PageDimensionsStoreState,
  PdfViewerProvider,
  usePageDimensions,
  usePageDimensionsStore,
  usePageHeight,
  useResetStore,
  useSetPageDimensions,
  useSetPageHeight,
} from '@/components/common/PdfViewerV2/PdfViewer/context'
import {
  type PdfSearchBounds,
  type PdfSearchMatch,
  searchPdfDocument,
} from '@/components/common/PdfViewerV2/PdfViewer/search'
import {
  PDF_CHUNK_BYTES,
  usePdfRangeFile,
} from '@/components/common/PdfViewerV2/rangeTransport'
import { OfficeViewerFindBar } from '@/components/office/OfficeViewer/OfficeViewerFindBar'
import { useOfficeViewerFindKeyboard } from '@/components/office/OfficeViewer/OfficeViewerFindBar/useOfficeViewerFindKeyboard'

import { toRgba } from '@/helpers/color'
import { SweetspotError } from '@/helpers/error'
import { getStableFileUrlKey } from '@/helpers/file'

import { useHandleError } from '@/hooks/common/useErrorHandler'
import useStableCallback from '@/hooks/common/useEventEffect'

import type { BoundingBox, Point } from '@/types/chat/Source'

// The worker is bundled and served from our own origin: the FedRAMP console's
// enforced CSP (worker-src 'self' blob:) blocks cross-origin CDN workers.
pdfjs.GlobalWorkerOptions.workerSrc = new URL(
  'pdfjs-dist/legacy/build/pdf.worker.min.mjs',
  import.meta.url,
).toString()

export const DEFAULT_WIDTH = 800
export const DEFAULT_HEIGHT = 1035
export const PAGE_GAP_PX = 12
const CITATION_SCROLL_VIEWPORT_OFFSET_RATIO = 0.18
const CITATION_SCROLL_MIN_TOP_PADDING_PX = 48
const CITATION_SCROLL_MAX_FRAME_ATTEMPTS = 8
const SCROLL_POSITION_EMIT_THRESHOLD_PX = 8
// Stable identity for pages with no citations — keeps `MemoizedPage`'s shallow
// equality check from busting on every parent rerender during scroll.
const EMPTY_CITATIONS: Citation[] = []
const EMPTY_SEARCH_MATCHES: PdfSearchMatch[] = []
const NOOP_PAGE_CHANGE = () => {}
const SEARCH_SCROLL_MAX_FRAME_ATTEMPTS = 8
// A reopened selection card re-finds its captured text by first line (see
// `highlightText`); the cap keeps a pathological single-line capture from
// scanning the document for a needle nobody could visually confirm anyway.
const CHIP_HIGHLIGHT_QUERY_MAX_CHARS = 200
// Above this many line rects a captured selection stops carrying its exact
// boxes (the card would balloon) and reopening falls back to the text
// re-find path.
const SELECTION_RECTS_MAX = 120
// How long mounted pages wait on a caller who said the scale isn't ready yet.
// The first-page measurement that feeds a fit-width caller normally lands a
// few hundred milliseconds after load; if it never comes (the measurement is
// allowed to fail), pages render at the un-fitted scale rather than never.
const PAGE_MOUNT_FALLBACK_MS = 2000
/** When the rest of a range-read file starts arriving if page one never draws. */
const FILL_FALLBACK_DELAY_MS = 1000

export interface Citation {
  /** Source id used to render the citation number badge on hover. */
  id?: number
  file_page: number
  bounding_box?: BoundingBox
  color?: string
}

export interface ScrollToPageOptions {
  preferCitationOffset?: boolean
  targetCitationId?: number
}

interface PendingScrollToPage {
  pageNumber: number
  options?: ScrollToPageOptions
}

interface PageDimensions {
  widthInches: number
  heightInches: number
}

interface LoadingPageProps {
  height?: number
  width?: number
  fileIsLarge?: boolean
}

export const LoadingPage: React.FC<LoadingPageProps> = ({
  height = DEFAULT_HEIGHT,
  width = DEFAULT_WIDTH,
  fileIsLarge = false,
}) => {
  return (
    <div className="flex items-center justify-center w-full">
      <div
        className="flex justify-center pt-36 min-w-[200px]"
        style={{ height, width }}
      >
        <span className="text-sm text-primary-500 dark:text-primary-400">
          {fileIsLarge ? 'Loading large file...' : 'Loading...'}
        </span>
      </div>
    </div>
  )
}

interface PagePlaceholderProps {
  height?: number
  width?: number
}

// The blank page-shaped box a held or not-yet-fitted page keeps its place
// with. Loading copy belongs to the document-level placeholder only.
const PagePlaceholder: React.FC<PagePlaceholderProps> = ({
  height = DEFAULT_HEIGHT,
  width = DEFAULT_WIDTH,
}) => {
  return (
    <div className="flex items-center justify-center w-full">
      <div className="min-w-[200px] bg-white" style={{ height, width }} />
    </div>
  )
}

export const ErrorPage: React.FC = () => {
  return (
    <div className="flex items-center justify-center w-full">
      <div
        className="flex justify-center pt-36 w-full"
        // style={{ height, width }}
      >
        <span className="font-medium text-sm text-red-600 dark:text-red-400">
          Failed to load PDF
        </span>
      </div>
    </div>
  )
}

interface PdfViewerResetListenerProps {
  inputFileUrl: string
}

export const PdfViewerResetListener: React.FC<PdfViewerResetListenerProps> = ({
  inputFileUrl,
}) => {
  const resetStore = useResetStore()
  const stableKey = getStableFileUrlKey(inputFileUrl)

  useEffect(() => {
    resetStore()
  }, [resetStore, stableKey])

  return null
}

export interface PdfViewerHandle {
  /**
   * Scroll to a specific page number
   * @param pageNumber The page number to scroll to (1-based index)
   */
  scrollToPage: (pageNumber: number, options?: ScrollToPageOptions) => void
  /**
   * Get the bounding client rect of the scroll container
   */
  getBoundingClientRect: () => DOMRect | null
}

/** One page's slice of a captured selection highlight, in the page-local
 * scale-1 units PdfSearchHighlights paints (see PdfSearchBounds). */
export interface PdfSelectionPageRects {
  pageNumber: number
  rects: PdfSearchBounds[]
}

export interface PdfTextSelection {
  text: string
  startPage: number
  endPage: number
  /**
   * The highlight's painted rects, measured off the live selection. Baked
   * into the selection card so reopening it repaints the exact boxes instead
   * of re-finding the text. Omitted when the selection is too fragmented to
   * be worth carrying (see SELECTION_RECTS_MAX).
   */
  pageRects?: PdfSelectionPageRects[]
}

export type PageRenderer = (
  props: PageProps & { fileIsLarge?: boolean },
) => React.ReactNode

export interface PdfViewerProps {
  inputFileUrl: string
  pageScale?: number
  /**
   * False while the caller is still deriving `pageScale` — a fit-width viewer
   * before the first page's real width has arrived. Pages stay unmounted
   * behind sized placeholders until it turns true, so nothing rasterizes at a
   * scale that is about to be replaced.
   */
  isPageScaleReady?: boolean
  centerOnZoom?: boolean
  citations?: Citation[]
  activeCitationId?: number
  activeCitationActivationKey?: number
  initialScrollTop?: number
  onScrollPositionChange?: (scrollTop: number) => void
  onFirstPageDimensions?: (pageDimensions: PageDimensions) => void
  onFirstPageRenderSuccess?: () => void
  onLoadSuccess?: (numPages: number) => void
  onLoadError?: () => void
  onPageChange?: (pageNumber: number) => void
  onTextSelectionChange?: (selection: PdfTextSelection | null) => void
  className?: string
  fileSize?: number
  /**
   * The exact size of the bytes at `inputFileUrl` — not given for a rendition
   * of some other file. With it the viewer reads the PDF by range and draws
   * page one before the rest has arrived.
   */
  byteLength?: number
  /**
   * A new presigned URL for the file at `inputFileUrl`. Range reads go on
   * long after the viewer opens — the rest of the file behind page one, a
   * page the reader jumps to — and outlive a URL minted for a quick download.
   */
  refreshUrl?: () => Promise<string>
  overscan?: number
  pageRenderer?: PageRenderer
  /**
   * Captured flowing text to re-highlight when the viewer opens (a selection
   * card reopening its PDF). The first line is re-found via the find
   * machinery and painted as the active match; `highlightPage` disambiguates
   * repeated phrases by preferring the match nearest that 1-based page.
   */
  highlightText?: string | null
  /** Changes when the caller explicitly requests the same highlight again. */
  highlightKey?: number
  /** 1-based page the highlighted selection was captured on. */
  highlightPage?: number | null
  /**
   * Exact highlight boxes captured with the selection (see
   * PdfTextSelection.pageRects). When present they are repainted directly
   * and `highlightText`/`highlightPage` serve only as the fallback.
   */
  highlightRects?: PdfSelectionPageRects[] | null
  /**
   * The reopened card's paint was replaced by an in-viewer gesture (collapse
   * on the page or a fresh selection), keyed by `highlightKey`, so the chip
   * that armed the highlight can dim with it. Fires per gesture; keyed
   * consumers no-op after the first.
   */
  onHighlightDismissed?: (key: number) => void
  /**
   * Bumped by the chat surface's explicit deselect gestures (Escape, a press
   * outside the viewer). The popover collapses the DOM ranges itself, but
   * this viewer's capture deliberately survives outside-the-viewer selection
   * moves, so the bump is what echoes the clear: the capture empties and a
   * reopened card's paint comes down. Same channel the office viewers
   * consume.
   */
  selectionResetNonce?: number
  /**
   * Optional ref to control the viewer programmatically
   */
  viewerRef?: React.RefObject<PdfViewerHandle>
}

export const PdfViewer = memo(
  forwardRef<PdfViewerHandle, PdfViewerProps>((props, ref) => {
    // Stabilize the URL: only update when the S3 object path changes,
    // not when the presigned signature rotates on refetch.
    const stableUrlRef = useRef(props.inputFileUrl)
    const incomingKey = getStableFileUrlKey(props.inputFileUrl)
    if (getStableFileUrlKey(stableUrlRef.current) !== incomingKey) {
      stableUrlRef.current = props.inputFileUrl
    }

    return (
      <PdfViewerProvider>
        <BasePdfViewer
          {...props}
          inputFileUrl={stableUrlRef.current}
          ref={ref}
        />
      </PdfViewerProvider>
    )
  }),
  (prevProps, nextProps) => {
    // Compare inputFileUrl by S3 path only (ignore presigned query params)
    if (
      getStableFileUrlKey(prevProps.inputFileUrl) !==
      getStableFileUrlKey(nextProps.inputFileUrl)
    )
      return false

    // Shallow-compare the rest
    const { inputFileUrl: _prev, ...prevRest } = prevProps
    const { inputFileUrl: _next, ...nextRest } = nextProps
    const prevKeys = Object.keys(prevRest)
    const nextKeys = Object.keys(nextRest)
    if (prevKeys.length !== nextKeys.length) return false
    return nextKeys.every((k) =>
      Object.is(
        prevRest[k as keyof typeof prevRest],
        nextRest[k as keyof typeof nextRest],
      ),
    )
  },
)

const BasePdfViewer = memo(
  forwardRef<PdfViewerHandle, PdfViewerProps>(
    (
      {
        inputFileUrl,
        pageScale: requestedPageScale = 1,
        isPageScaleReady = true,
        centerOnZoom = false,
        citations,
        activeCitationId,
        activeCitationActivationKey,
        initialScrollTop,
        onScrollPositionChange,
        onFirstPageDimensions,
        onFirstPageRenderSuccess,
        onLoadSuccess,
        onLoadError,
        onPageChange,
        onTextSelectionChange,
        className,
        viewerRef,
        fileSize,
        byteLength,
        refreshUrl,
        overscan = 3,
        pageRenderer,
        highlightText,
        highlightKey,
        highlightPage,
        highlightRects,
        onHighlightDismissed,
        selectionResetNonce,
      }: PdfViewerProps,
      ref,
    ) => {
      // Held steady for the length of a pane drag, so the document refits when
      // the divider stops rather than rasterizing every page on every frame.
      const { pageScale, isSettling: isPageScaleSettling } =
        useSettledPageScale(requestedPageScale)

      // Pages mount only once the caller's scale is ready and settled, so the
      // first raster of each page is at the scale the reader will keep. Opens
      // late (an effect after the settle commit) and that is the point: every
      // earlier commit still shows placeholders, whatever scale it carried.
      const [arePagesLive, setArePagesLive] = useState(isPageScaleReady)

      const [numPages, setNumPages] = useState<number>()
      const [estimatedPageDimensions, setEstimatedPageDimensions] =
        useState<PageDimensions | null>(null)
      const [pdfDocument, setPdfDocument] =
        useState<pdfjs.PDFDocumentProxy | null>(null)
      const [activeSearchMatchIndex, setActiveSearchMatchIndex] = useState(0)
      const [findQuery, setFindQuery] = useState('')
      const [isFindOpen, setIsFindOpen] = useState(false)
      const [isSearching, setIsSearching] = useState(false)
      const [searchMatches, setSearchMatches] = useState<PdfSearchMatch[]>([])
      const [searchRequestGeneration, setSearchRequestGeneration] = useState(0)
      // The single match a reopened selection card re-highlights (see
      // `highlightText`); rendered through the find-highlight painter while
      // the find bar is closed, so the two never fight over the overlay.
      const [chipHighlightFound, setChipHighlightFound] = useState<{
        key: number | undefined
        text: string
        match: PdfSearchMatch
      } | null>(null)
      const chipHighlightMatch =
        chipHighlightFound !== null &&
        chipHighlightFound.key === highlightKey &&
        chipHighlightFound.text === highlightText
          ? chipHighlightFound.match
          : null
      // Explicit deselect for the chip highlight: a collapse click or a new
      // selection inside the viewer dismisses the current card's paint, and a
      // repeat card click (`highlightKey` bump) brings it back - the same
      // machine the flowing-text viewers run in useTextSelectionHighlights.
      // Keyed on `highlightKey` so no reset effect is needed.
      const [chipDismissedForKey, setChipDismissedForKey] = useState<
        number | null
      >(null)
      const chipHighlightDismissed =
        chipDismissedForKey !== null &&
        chipDismissedForKey === (highlightKey ?? -1)

      const { handleError } = useHandleError()

      const containerRef = useRef<HTMLDivElement>(null)
      const findInputRef = useRef<HTMLInputElement>(null)
      const pendingScrollToPageRef = useRef<PendingScrollToPage | null>(null)
      const restoreScrollFrameRef = useRef<number | null>(null)
      const searchRequestGenerationRef = useRef(0)
      const searchScrollFrameRef = useRef<number | null>(null)
      const pendingScrollTopRef = useRef<number | null>(null)
      const lastEmittedScrollTopRef = useRef<number | null>(null)
      const restoredScrollFileUrlKeyRef = useRef<string | null>(null)
      const onScrollPositionChangeRef = useRef(onScrollPositionChange)
      const viewerRootRef = useRef<HTMLDivElement>(null)
      const fileUrlKey = getStableFileUrlKey(inputFileUrl)
      const previousFileUrlKeyRef = useRef(fileUrlKey)
      // A file whose range reads fail is read whole instead, as it was before.
      const [rangeFailedKey, setRangeFailedKey] = useState<string | null>(null)
      const { file: pdfFile, fillRest } = usePdfRangeFile({
        url: inputFileUrl,
        size: rangeFailedKey === fileUrlKey ? undefined : byteLength,
        fileKey: fileUrlKey,
        refreshUrl,
        onError: () => setRangeFailedKey(fileUrlKey),
      })
      // The rest follows page one, or a second after the document opens when
      // the reader starts further in and page one never draws.
      const handleFirstPageRenderSuccess = useCallback(() => {
        fillRest()
        onFirstPageRenderSuccess?.()
      }, [fillRest, onFirstPageRenderSuccess])
      onScrollPositionChangeRef.current = onScrollPositionChange
      // Read through a ref inside the selection listener: a pageScale dep
      // would tear the listener down on every zoom settle, dropping a
      // throttled emit (and the deselect it carries) scheduled in between.
      const pageScaleRef = useRef(pageScale)
      pageScaleRef.current = pageScale
      const stableOnTextSelectionChange = useStableCallback(
        (selection: PdfTextSelection | null) => {
          onTextSelectionChange?.(selection)
        },
      )
      const dismissChipHighlight = useStableCallback(() => {
        setChipDismissedForKey(highlightKey ?? -1)
        if (highlightKey !== undefined) onHighlightDismissed?.(highlightKey)
      })
      // Escape / outside-press deselect: see selectionResetNonce. Change-only
      // (the mount value is history, not a gesture).
      const lastSelectionResetNonceRef = useRef(selectionResetNonce)
      useEffect(() => {
        if (
          selectionResetNonce === undefined ||
          lastSelectionResetNonceRef.current === selectionResetNonce
        ) {
          lastSelectionResetNonceRef.current = selectionResetNonce
          return
        }
        lastSelectionResetNonceRef.current = selectionResetNonce
        dismissChipHighlight()
        stableOnTextSelectionChange(null)
      }, [
        selectionResetNonce,
        dismissChipHighlight,
        stableOnTextSelectionChange,
      ])
      const textSelectionChangeEnabled = onTextSelectionChange !== undefined
      // The listener also drives deselect for a card highlight, so it must
      // run whenever one could be painted - even with no selection callback
      // (mirrors useTextSelectionHighlights in the flowing-text viewers).
      const chipDismissalEnabled =
        Boolean(highlightText) || (highlightRects?.length ?? 0) > 0
      useEffect(() => {
        if (!textSelectionChangeEnabled && !chipDismissalEnabled) return

        let timeoutId: number | null = null
        const emitSelection = () => {
          timeoutId = null
          const selection = window.getSelection()
          const container = containerRef.current
          if (!selection || !container) return
          // A selection living outside the viewer (composer focus, clicking
          // Send, app-level selections elsewhere in the page) says nothing
          // about the PDF capture: keep it. Clearing is explicit — a collapse
          // *inside* the viewer (click on the page) or the composer chip's
          // dismiss button.
          if (
            !selection.anchorNode ||
            !selection.focusNode ||
            !container.contains(selection.anchorNode) ||
            !container.contains(selection.focusNode)
          ) {
            return
          }
          if (selection.isCollapsed) {
            dismissChipHighlight()
            stableOnTextSelectionChange(null)
            return
          }

          const getPageNumber = (node: Node) => {
            const element = node instanceof Element ? node : node.parentElement
            const page = element?.closest('[data-page-number]')
            const pageNumber = page?.getAttribute('data-page-number')
            return pageNumber ? Number(pageNumber) : undefined
          }
          const startPage = getPageNumber(selection.anchorNode)
          const endPage = getPageNumber(selection.focusNode)
          if (startPage === undefined || endPage === undefined) {
            // In-viewer selection that isn't page text (find bar, overlays):
            // says nothing about the page capture, so keep it.
            return
          }

          // The user's own selection replaces the reopened card's paint.
          dismissChipHighlight()
          // Without a capture consumer there is nothing else to compute:
          // skip the layout reads (getClientRects + per-page
          // getBoundingClientRect) that only feed the emitted payload.
          if (!textSelectionChangeEnabled) return

          // Measure the highlight's exact boxes off the live selection, in
          // page-local scale-1 units (divide the client rects by the current
          // scale), so a card made from this capture can repaint them without
          // re-finding the text. Selections shattered into too many rects
          // travel without boxes and reopen through the text fallback.
          let pageRects: PdfSelectionPageRects[] | undefined
          const clientRects = Array.from(
            selection.getRangeAt(0).getClientRects(),
          ).filter((rect) => rect.width > 0 && rect.height > 0)
          if (
            clientRects.length > 0 &&
            clientRects.length <= SELECTION_RECTS_MAX
          ) {
            const rectsByPage = new Map<number, PdfSearchBounds[]>()
            const pageElements = Array.from(
              container.querySelectorAll<HTMLElement>('[data-page-number]'),
            )
            const pages = pageElements.map((element) => ({
              pageNumber: Number(element.getAttribute('data-page-number')),
              rect: element.getBoundingClientRect(),
            }))
            for (const rect of clientRects) {
              const centerX = rect.left + rect.width / 2
              const centerY = rect.top + rect.height / 2
              const page = pages.find(
                (candidate) =>
                  centerX >= candidate.rect.left &&
                  centerX <= candidate.rect.right &&
                  centerY >= candidate.rect.top &&
                  centerY <= candidate.rect.bottom,
              )
              if (!page || !Number.isFinite(page.pageNumber)) continue
              const scale = pageScaleRef.current
              const bounds: PdfSearchBounds = {
                left: (rect.left - page.rect.left) / scale,
                top: (rect.top - page.rect.top) / scale,
                width: rect.width / scale,
                height: rect.height / scale,
              }
              const existing = rectsByPage.get(page.pageNumber)
              if (existing) {
                existing.push(bounds)
              } else {
                rectsByPage.set(page.pageNumber, [bounds])
              }
            }
            if (rectsByPage.size > 0) {
              pageRects = Array.from(rectsByPage.entries())
                .sort(([a], [b]) => a - b)
                .map(([pageNumber, rects]) => ({ pageNumber, rects }))
            }
          }

          stableOnTextSelectionChange({
            text: selection.toString(),
            startPage: Math.min(startPage, endPage),
            endPage: Math.max(startPage, endPage),
            ...(pageRects ? { pageRects } : {}),
          })
        }
        // Leading + trailing throttle (mirrors the markdown/html viewers): a
        // trailing-only debounce resets per mousemove, hiding the composer
        // chip until the drag pauses. First event emits right away, later
        // ones at most every 100ms with a trailing emit for the final range.
        let lastEmitAt = 0
        const handleSelectionChange = () => {
          if (timeoutId !== null) return
          const delay = Math.max(0, 100 - (Date.now() - lastEmitAt))
          timeoutId = window.setTimeout(() => {
            lastEmitAt = Date.now()
            emitSelection()
          }, delay)
        }
        document.addEventListener('selectionchange', handleSelectionChange)
        return () => {
          document.removeEventListener('selectionchange', handleSelectionChange)
          if (timeoutId !== null) window.clearTimeout(timeoutId)
        }
      }, [
        chipDismissalEnabled,
        dismissChipHighlight,
        stableOnTextSelectionChange,
        textSelectionChangeEnabled,
      ])
      const deferredFindQuery = useDeferredValue(findQuery)
      const invalidateSearchRequest = useCallback(() => {
        searchRequestGenerationRef.current += 1
        setSearchRequestGeneration(searchRequestGenerationRef.current)
      }, [])
      const activeSearchMatch =
        searchMatches[activeSearchMatchIndex] ?? undefined
      const searchMatchesByPage = useMemo(() => {
        const matchesByPage = new Map<number, PdfSearchMatch[]>()
        for (const match of searchMatches) {
          const pageMatches = matchesByPage.get(match.pageNumber)
          if (pageMatches) {
            pageMatches.push(match)
          } else {
            matchesByPage.set(match.pageNumber, [match])
          }
        }
        return matchesByPage
      }, [searchMatches])
      // What a reopened selection card paints, exact-boxes first:
      //  - `highlightRects` (the card baked its measured boxes, `pdf:bbox`):
      //    synthesize one match per page straight from the boxes — no text
      //    search, guaranteed to land on the original ink.
      //  - `highlightText` re-find (`pdf:p12` cards, or bbox parsing failed):
      //    the single re-found `chipHighlightMatch`.
      // Rendered through the find-highlight painter; while the find bar is
      // open it owns the overlay and the chip yields entirely.
      const chipRectMatches = useMemo(() => {
        if (!highlightRects || highlightRects.length === 0)
          return EMPTY_SEARCH_MATCHES
        return highlightRects.map((page) => ({
          id: `chip:${page.pageNumber}`,
          pageNumber: page.pageNumber,
          bounds: page.rects.map((rect) => ({ ...rect })),
          matchStart: 0,
          matchEnd: 0,
        }))
      }, [highlightRects])
      const chipHighlightMatches = useMemo(() => {
        if (isFindOpen || chipHighlightDismissed) return EMPTY_SEARCH_MATCHES
        if (chipRectMatches.length > 0) return chipRectMatches
        return chipHighlightMatch ? [chipHighlightMatch] : EMPTY_SEARCH_MATCHES
      }, [
        chipHighlightMatch,
        chipRectMatches,
        chipHighlightDismissed,
        isFindOpen,
      ])

      const fileIsLarge = fileSize !== undefined && fileSize > 2000000
      const estimatedPageWidth = estimatedPageDimensions
        ? inchesToPixels({
            inches: estimatedPageDimensions.widthInches,
            pageScale,
          })
        : DEFAULT_WIDTH * pageScale
      const estimatedPageHeight = estimatedPageDimensions
        ? inchesToPixels({
            inches: estimatedPageDimensions.heightInches,
            pageScale,
          })
        : DEFAULT_HEIGHT * pageScale

      const virtualizer = useVirtualizer({
        count: numPages || 0,
        getScrollElement: () => containerRef.current,
        estimateSize: () => estimatedPageHeight,
        overscan,
        gap: PAGE_GAP_PX,
        paddingStart: PAGE_GAP_PX,
        paddingEnd: PAGE_GAP_PX,
      })

      const virtualItems = virtualizer.getVirtualItems()
      // `range` is the window before overscan widens it: the pages actually on
      // screen, rather than the ones rendered ahead so scrolling has something
      // to show. Text and annotation layers follow it, since neither is worth
      // building for a page nobody can reach yet. Null until the first measure.
      const onScreenRange = virtualizer.range

      // Group citations by page once per `citations` change so each page's
      // citations array has a stable reference across rerenders. Without this,
      // every `virtualItems.map` pass produces fresh arrays (especially `[]` for
      // pages with no citations), busting `MemoizedPage`'s shallow equality and
      // forcing every page to rerender on every scroll tick.
      const citationsByPage = useMemo(() => {
        const map = new Map<number, Citation[]>()
        if (!citations) return map
        for (const citation of citations) {
          const list = map.get(citation.file_page)
          if (list) {
            list.push(citation)
          } else {
            map.set(citation.file_page, [citation])
          }
        }
        return map
      }, [citations])
      // Read through refs when scrolling: a dep on either would hand every
      // scroll effect a new `scrollToPage` on a zoom or a fresh citations
      // array, pulling the reader back to a target they already left.
      const citationsByPageRef = useRef(citationsByPage)
      citationsByPageRef.current = citationsByPage

      const activeCitationTarget = useMemo(() => {
        if (activeCitationId === undefined || !citations) return null
        return (
          citations.find((citation) => citation.id === activeCitationId) ?? null
        )
      }, [activeCitationId, citations])

      const scrollCitationIntoComfortableView = useCallback(
        (pageNumber: number, targetCitationId?: number) => {
          const pageCitations =
            citationsByPageRef.current.get(pageNumber) ?? EMPTY_CITATIONS
          const citation =
            targetCitationId === undefined
              ? pageCitations.find((candidate) => candidate.bounding_box)
              : (pageCitations.find(
                  (candidate) =>
                    candidate.id === targetCitationId && candidate.bounding_box,
                ) ?? pageCitations.find((candidate) => candidate.bounding_box))
          if (!citation?.bounding_box) return true

          const container = containerRef.current
          if (!container) {
            return false
          }

          const pageNode = container.querySelector<HTMLElement>(
            `[data-page-number="${pageNumber}"]`,
          )
          if (!pageNode) {
            return false
          }

          const containerRect = container.getBoundingClientRect()
          const pageRect = pageNode.getBoundingClientRect()
          const citationNode =
            targetCitationId === undefined
              ? null
              : container.querySelector<HTMLElement>(
                  `[data-citation-id="${targetCitationId}"]`,
                )
          const yCoords = citation.bounding_box.map((point) => point[1])
          const citationTop = Math.min(...yCoords) * 72 * pageScaleRef.current
          const citationViewportTop =
            citationNode !== null
              ? citationNode.getBoundingClientRect().top - containerRect.top
              : pageRect.top - containerRect.top + citationTop
          const desiredViewportTop = Math.max(
            CITATION_SCROLL_MIN_TOP_PADDING_PX,
            containerRect.height * CITATION_SCROLL_VIEWPORT_OFFSET_RATIO,
          )
          const nextScrollTop =
            container.scrollTop + citationViewportTop - desiredViewportTop

          container.scrollTo({
            top: Math.max(0, nextScrollTop),
            behavior: 'auto',
          })
          return true
        },
        [],
      )

      const scrollCitationIntoComfortableViewWhenReady = useCallback(
        (
          pageNumber: number,
          targetCitationId: number | undefined,
          attemptsRemaining = CITATION_SCROLL_MAX_FRAME_ATTEMPTS,
        ) => {
          requestAnimationFrame(() => {
            const didScroll = scrollCitationIntoComfortableView(
              pageNumber,
              targetCitationId,
            )
            if (didScroll || attemptsRemaining <= 0) {
              return
            }

            scrollCitationIntoComfortableViewWhenReady(
              pageNumber,
              targetCitationId,
              attemptsRemaining - 1,
            )
          })
        },
        [scrollCitationIntoComfortableView],
      )

      // TODO: is this the best way to handle the justify-center-safe overflow?
      const selectFirstVirtualItemPageWidth = useCallback(
        (state: PageDimensionsStoreState) => {
          // have the width of the parent container of the virtual items be
          // the width of the first virtual item adjusted for the page scale;
          const firstVirtualItem = virtualItems[0]

          // +1 since we track by page number, not index
          return firstVirtualItem &&
            state.pageDimensions[firstVirtualItem.index + 1]
            ? inchesToPixels({
                inches:
                  state.pageDimensions[firstVirtualItem.index + 1]!.widthInches,
                pageScale,
              })
            : estimatedPageWidth
        },
        [estimatedPageWidth, pageScale, virtualItems],
      )

      const firstVirtualItemPageWidth = usePageDimensionsStore(
        selectFirstVirtualItemPageWidth,
      )

      const options = useMemo(
        () => ({
          cMapUrl: '/cmaps/',
          standardFontDataUrl: '/standard_fonts/',
          rangeChunkSize: PDF_CHUNK_BYTES,
        }),
        [],
      )

      const handleOnLoadSuccess = useCallback(
        (document: pdfjs.PDFDocumentProxy) => {
          window.setTimeout(fillRest, FILL_FALLBACK_DELAY_MS)
          setPdfDocument(document)
          setNumPages(document.numPages)
          onLoadSuccess?.(document.numPages)
          virtualizer.measure()

          void document
            .getPage(1)
            .then((firstPage) => {
              // The viewer may have moved on to another file while this page
              // was being fetched; its measurements belong to the old one.
              if (previousFileUrlKeyRef.current !== fileUrlKey) return
              const originalViewport = firstPage.getViewport({ scale: 1 })
              const firstPageDimensions = {
                widthInches: originalViewport.width / 72,
                heightInches: originalViewport.height / 72,
              }
              setEstimatedPageDimensions(firstPageDimensions)
              onFirstPageDimensions?.(firstPageDimensions)
            })
            .catch(() => {
              // Keep PDF loading non-fatal if optional auto-fit measurement fails.
            })
        },
        [
          fileUrlKey,
          fillRest,
          onFirstPageDimensions,
          onLoadSuccess,
          virtualizer,
        ],
      )

      useEffect(() => {
        // Pages go live at most once per document; only the file-switch reset
        // below puts them back behind placeholders. Reacting to a readiness
        // flip here would unmount rendered pages and cancel this fallback.
        if (arePagesLive) return
        if (isPageScaleReady && !isPageScaleSettling) {
          setArePagesLive(true)
          return
        }
        // The fallback clock runs only while a loaded document is waiting on
        // its caller's fit, not while the file itself is still downloading.
        if (!pdfDocument) return

        const fallbackHandle = window.setTimeout(
          () => setArePagesLive(true),
          PAGE_MOUNT_FALLBACK_MS,
        )
        return () => window.clearTimeout(fallbackHandle)
      }, [arePagesLive, isPageScaleReady, isPageScaleSettling, pdfDocument])

      useEffect(() => {
        if (deferredFindQuery !== findQuery) {
          setIsSearching(Boolean(findQuery && pdfDocument))
          return
        }
        if (!deferredFindQuery || !pdfDocument) {
          setSearchMatches([])
          setActiveSearchMatchIndex(0)
          setIsSearching(false)
          return
        }

        let cancelled = false
        const abortController = new AbortController()
        const requestGeneration = searchRequestGeneration
        setIsSearching(true)
        void searchPdfDocument(pdfDocument, deferredFindQuery, {
          signal: abortController.signal,
        })
          .then((nextMatches) => {
            if (
              cancelled ||
              requestGeneration !== searchRequestGenerationRef.current
            ) {
              return
            }
            setSearchMatches(nextMatches)
            setActiveSearchMatchIndex(0)
            setIsSearching(false)
          })
          .catch(() => {
            if (
              cancelled ||
              requestGeneration !== searchRequestGenerationRef.current
            ) {
              return
            }
            setSearchMatches([])
            setActiveSearchMatchIndex(0)
            setIsSearching(false)
          })

        return () => {
          cancelled = true
          abortController.abort()
        }
      }, [deferredFindQuery, findQuery, pdfDocument, searchRequestGeneration])
      // Re-find a reopened selection card's captured text once the document
      // is ready. Multi-line captures re-find by their first non-empty line
      // (the page text joins items the way the text layer breaks them, so a
      // literal cross-line match is fragile) with one trailing truncation
      // ellipsis stripped; when the phrase repeats, the match nearest the
      // captured page wins.
      useEffect(() => {
        // Baked boxes paint directly — no text re-find to race or miss.
        if (!pdfDocument || !highlightText || chipRectMatches.length > 0) {
          setChipHighlightFound(null)
          return
        }
        const firstLine = highlightText
          .split('\n')
          .map((line) => line.trim())
          .find((line) => line.length > 0)
        const query = firstLine
          ?.replace(/(?:\.\.\.|…)$/, '')
          .trim()
          .slice(0, CHIP_HIGHLIGHT_QUERY_MAX_CHARS)
        if (!query) {
          setChipHighlightFound(null)
          return
        }
        let cancelled = false
        const abortController = new AbortController()
        void searchPdfDocument(pdfDocument, query, {
          signal: abortController.signal,
        })
          .then((matches) => {
            if (cancelled) return
            if (matches.length === 0) {
              setChipHighlightFound(null)
              return
            }
            const targetPage = highlightPage ?? matches[0]!.pageNumber
            let best = matches[0]!
            for (const match of matches) {
              if (
                Math.abs(match.pageNumber - targetPage) <
                Math.abs(best.pageNumber - targetPage)
              ) {
                best = match
              }
            }
            setChipHighlightFound({
              key: highlightKey,
              text: highlightText,
              match: best,
            })
          })
          .catch(() => {
            if (!cancelled) setChipHighlightFound(null)
          })
        return () => {
          cancelled = true
          abortController.abort()
        }
      }, [
        pdfDocument,
        highlightText,
        highlightKey,
        highlightPage,
        chipRectMatches,
      ])

      const handleLoadError = useCallback(
        (error: Error) => {
          onLoadError?.()
          handleError(
            new SweetspotError({
              name: 'PDF_LOAD_ERROR',
              message: `Error loading PDF in PdfViewerV2: ${error.message}`,
            }),
          )
        },
        [handleError, onLoadError],
      )

      const openFind = useCallback(() => {
        setIsFindOpen(true)
        window.requestAnimationFrame(() => {
          findInputRef.current?.focus({ preventScroll: true })
          findInputRef.current?.select()
        })
      }, [])

      const closeFind = useCallback(() => {
        setIsFindOpen(false)
        window.requestAnimationFrame(() => {
          viewerRootRef.current?.focus({ preventScroll: true })
        })
      }, [])

      const handleViewerKeyDown = useOfficeViewerFindKeyboard({
        isOpen: isFindOpen,
        onClose: closeFind,
        onOpen: openFind,
        viewerRootRef,
      })

      const handleViewerPointerDownCapture = useCallback(
        (event: React.PointerEvent<HTMLDivElement>) => {
          const target = event.target
          if (
            target instanceof Element &&
            target.closest(
              'input, textarea, select, button, a, [contenteditable="true"]',
            )
          ) {
            return
          }
          event.currentTarget.focus({ preventScroll: true })
        },
        [],
      )

      const moveSearchMatch = useCallback(
        (direction: -1 | 1) => {
          if (searchMatches.length === 0) {
            return
          }
          setActiveSearchMatchIndex(
            (currentIndex) =>
              (currentIndex + direction + searchMatches.length) %
              searchMatches.length,
          )
        },
        [searchMatches.length],
      )

      const handleNextSearchMatch = useCallback(() => {
        moveSearchMatch(1)
      }, [moveSearchMatch])

      const handlePreviousSearchMatch = useCallback(() => {
        moveSearchMatch(-1)
      }, [moveSearchMatch])

      const handleFindQueryChange = useCallback(
        (nextQuery: string) => {
          invalidateSearchRequest()
          setFindQuery(nextQuery)
          setSearchMatches([])
          setActiveSearchMatchIndex(0)
          setIsSearching(Boolean(nextQuery && pdfDocument))
        },
        [invalidateSearchRequest, pdfDocument],
      )

      // Function to scroll to a specific page
      const scrollToPage = useCallback(
        (
          pageNumber: number,
          {
            preferCitationOffset = true,
            targetCitationId,
          }: ScrollToPageOptions = {},
        ) => {
          // Before the pages mount there is nothing to measure a citation
          // against, and the placeholders standing in for them are estimated
          // heights, so a jump made now lands somewhere else once they arrive.
          if (!numPages || !arePagesLive) {
            pendingScrollToPageRef.current = {
              pageNumber,
              options: { preferCitationOffset, targetCitationId },
            }
            return
          }

          if (pageNumber < 1 || pageNumber > numPages) {
            return
          }

          // Find the page in the virtual items if it's currently rendered
          const itemIndex = pageNumber - 1

          // Scroll to the page
          virtualizer.scrollToIndex(itemIndex, {
            align: 'start',
            behavior: 'auto',
          })

          if (preferCitationOffset) {
            scrollCitationIntoComfortableViewWhenReady(
              pageNumber,
              targetCitationId,
            )
          }

          onPageChange?.(pageNumber)
        },
        [
          arePagesLive,
          numPages,
          onPageChange,
          scrollCitationIntoComfortableViewWhenReady,
          virtualizer,
        ],
      )

      const scrollSearchMatchIntoViewWhenReady = useCallback(
        (attemptsRemaining = SEARCH_SCROLL_MAX_FRAME_ATTEMPTS): void => {
          searchScrollFrameRef.current = window.requestAnimationFrame(() => {
            searchScrollFrameRef.current = null
            const container = containerRef.current
            const activeHighlight =
              container?.querySelector<HTMLElement>(
                '[data-pdf-search-highlight="active"]',
              ) ?? null
            if (!container || !activeHighlight) {
              if (attemptsRemaining > 0) {
                scrollSearchMatchIntoViewWhenReady(attemptsRemaining - 1)
              }
              return
            }

            const containerRect = container.getBoundingClientRect()
            const highlightRect = activeHighlight.getBoundingClientRect()
            const desiredViewportTop = Math.max(
              CITATION_SCROLL_MIN_TOP_PADDING_PX,
              containerRect.height * CITATION_SCROLL_VIEWPORT_OFFSET_RATIO,
            )
            container.scrollTo({
              top: Math.max(
                0,
                container.scrollTop +
                  highlightRect.top -
                  containerRect.top -
                  desiredViewportTop,
              ),
              behavior: 'auto',
            })
          })
        },
        [],
      )

      useEffect(() => {
        if (!isFindOpen || !activeSearchMatch) {
          return
        }

        scrollToPage(activeSearchMatch.pageNumber, {
          preferCitationOffset: false,
        })
        scrollSearchMatchIntoViewWhenReady()

        return () => {
          if (searchScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(searchScrollFrameRef.current)
            searchScrollFrameRef.current = null
          }
        }
      }, [
        activeSearchMatch,
        isFindOpen,
        scrollSearchMatchIntoViewWhenReady,
        scrollToPage,
      ])
      // Bring a reopened card's highlight into view the same way an active
      // find match arrives: jump to its page (baked boxes know it up front,
      // a re-found match once the search lands), then glue the scroll to the
      // painted rect once it exists. Skipped while the find bar owns the
      // overlay. `highlightKey` re-runs it for the baked-box path, whose
      // inputs are otherwise stable — a repeat click on the same card must
      // bring the highlight back into view.
      useEffect(() => {
        const targetPage =
          chipRectMatches.length > 0
            ? chipRectMatches[0]!.pageNumber
            : chipHighlightMatch?.pageNumber
        if (isFindOpen || chipHighlightDismissed || targetPage === undefined) {
          return
        }

        scrollToPage(targetPage, {
          preferCitationOffset: false,
        })
        scrollSearchMatchIntoViewWhenReady()

        return () => {
          if (searchScrollFrameRef.current !== null) {
            window.cancelAnimationFrame(searchScrollFrameRef.current)
            searchScrollFrameRef.current = null
          }
        }
      }, [
        chipHighlightMatch,
        chipHighlightDismissed,
        highlightKey,
        chipRectMatches,
        isFindOpen,
        scrollSearchMatchIntoViewWhenReady,
        scrollToPage,
      ])

      useEffect(() => {
        if (!numPages || !activeCitationTarget) return

        scrollToPage(activeCitationTarget.file_page, {
          targetCitationId: activeCitationTarget.id,
        })
      }, [
        activeCitationActivationKey,
        activeCitationTarget?.id,
        numPages,
        scrollToPage,
      ])

      useEffect(() => {
        if (!numPages || !arePagesLive || !pendingScrollToPageRef.current)
          return

        const pendingScroll = pendingScrollToPageRef.current
        pendingScrollToPageRef.current = null
        scrollToPage(pendingScroll.pageNumber, pendingScroll.options)
      }, [arePagesLive, numPages, scrollToPage])

      useEffect(() => {
        if (previousFileUrlKeyRef.current === fileUrlKey) return

        previousFileUrlKeyRef.current = fileUrlKey
        invalidateSearchRequest()
        setPdfDocument(null)
        setArePagesLive(false)
        setFindQuery('')
        setIsFindOpen(false)
        setIsSearching(false)
        setSearchMatches([])
        setActiveSearchMatchIndex(0)
        setEstimatedPageDimensions(null)
        pendingScrollToPageRef.current = null
        pendingScrollTopRef.current = null
        lastEmittedScrollTopRef.current = null
        restoredScrollFileUrlKeyRef.current = null
        if (restoreScrollFrameRef.current !== null) {
          cancelAnimationFrame(restoreScrollFrameRef.current)
          restoreScrollFrameRef.current = null
        }
        if (searchScrollFrameRef.current !== null) {
          cancelAnimationFrame(searchScrollFrameRef.current)
          searchScrollFrameRef.current = null
        }
      }, [fileUrlKey, invalidateSearchRequest])

      useEffect(() => {
        if (!numPages || !containerRef.current) return
        if (!initialScrollTop || initialScrollTop <= 0) return
        if (restoredScrollFileUrlKeyRef.current === fileUrlKey) return
        // Both inputs to a page's height, and both arrive after numPages: the
        // measured dimensions replace a 1035px guess, and the fit replaces a
        // provisional scale. Restoring before either lands puts the offset on
        // the wrong page, and the key below stops it ever being retried.
        if (!arePagesLive || !estimatedPageDimensions) return

        restoredScrollFileUrlKeyRef.current = fileUrlKey
        restoreScrollFrameRef.current = requestAnimationFrame(() => {
          restoreScrollFrameRef.current = null
          if (!containerRef.current) return
          containerRef.current.scrollTop = initialScrollTop
        })
      }, [
        arePagesLive,
        estimatedPageDimensions,
        fileUrlKey,
        initialScrollTop,
        numPages,
      ])

      const flushPendingScrollPosition = useCallback(() => {
        const onChange = onScrollPositionChangeRef.current
        if (!onChange) return

        const nextScrollTop = pendingScrollTopRef.current
        pendingScrollTopRef.current = null
        if (nextScrollTop === null) return

        if (
          lastEmittedScrollTopRef.current !== null &&
          Math.abs(lastEmittedScrollTopRef.current - nextScrollTop) <
            SCROLL_POSITION_EMIT_THRESHOLD_PX
        ) {
          return
        }

        lastEmittedScrollTopRef.current = nextScrollTop
        onChange(nextScrollTop)
      }, [])

      const handleObservedScrollPositionChange = useCallback(
        (scrollTop: number) => {
          pendingScrollTopRef.current = scrollTop
          flushPendingScrollPosition()
        },
        [flushPendingScrollPosition],
      )

      // Reads the offset the virtualizer already caches on scroll rather than
      // the container's own: this runs a frame after the scroll, by which point
      // React has queued the pages that scroll brought on screen, and a
      // scrollTop read there flushes the whole document's pending layout —
      // ~15ms — for a layout the mount is about to invalidate again.
      const getCurrentPage = useCallback(() => {
        const viewportStart = virtualizer.scrollOffset
        const viewportHeight = virtualizer.scrollRect?.height
        if (viewportStart === null || !viewportHeight) return undefined

        const viewportEnd = viewportStart + viewportHeight
        let mostVisibleItem: { index: number; visibility: number } | undefined

        for (const item of virtualizer.getVirtualItems()) {
          const visibleHeight = Math.max(
            0,
            Math.min(item.end, viewportEnd) -
              Math.max(item.start, viewportStart),
          )
          const visibility = visibleHeight / item.size
          if (!mostVisibleItem || visibility > mostVisibleItem.visibility) {
            mostVisibleItem = { index: item.index, visibility }
          }
        }

        return mostVisibleItem ? mostVisibleItem.index + 1 : undefined
      }, [virtualizer])

      const handleScroll = usePdfScrollObserver({
        getCurrentPage,
        onPageChange: onPageChange ?? NOOP_PAGE_CHANGE,
        onScrollPositionChange: handleObservedScrollPositionChange,
        isReady: numPages !== undefined,
      })

      // Tells the text layers to get ready, on the theory that a reader who
      // brings the pointer to the page is about to reach for a line on it.
      const readerPresence = useReaderPresence()

      useEffect(
        () => () => {
          flushPendingScrollPosition()
          if (restoreScrollFrameRef.current !== null) {
            cancelAnimationFrame(restoreScrollFrameRef.current)
          }
          if (searchScrollFrameRef.current !== null) {
            cancelAnimationFrame(searchScrollFrameRef.current)
          }
        },
        [flushPendingScrollPosition],
      )

      const getBoundingClientRect = useCallback(() => {
        return containerRef.current?.getBoundingClientRect() ?? null
      }, [])

      // Expose the scrollToPage function through the ref
      useImperativeHandle(
        ref,
        () => ({
          scrollToPage,
          getBoundingClientRect,
        }),
        [getBoundingClientRect, scrollToPage],
      )

      useImperativeHandle(
        viewerRef,
        () => ({
          scrollToPage,
          getBoundingClientRect,
        }),
        [getBoundingClientRect, scrollToPage],
      )

      return (
        <>
          <PdfViewerResetListener inputFileUrl={inputFileUrl} />
          <div
            ref={viewerRootRef}
            key={fileUrlKey}
            className={twMerge(
              'relative flex w-full h-full divide-x divide-primary-200 focus:outline-none dark:divide-primary-800',
              className,
            )}
            onKeyDown={handleViewerKeyDown}
            onPointerDownCapture={handleViewerPointerDownCapture}
            tabIndex={0}
            data-pdf-viewer
            aria-label="PDF document viewer"
          >
            {isFindOpen ? (
              <OfficeViewerFindBar
                ref={findInputRef}
                activeMatchIndex={activeSearchMatchIndex}
                isSearching={isSearching}
                matchCount={searchMatches.length}
                onClose={closeFind}
                onNextMatch={handleNextSearchMatch}
                onPreviousMatch={handlePreviousSearchMatch}
                onQueryChange={handleFindQueryChange}
                query={findQuery}
              />
            ) : null}
            <Document
              key={fileUrlKey}
              file={pdfFile}
              onLoadSuccess={handleOnLoadSuccess}
              onLoadError={handleLoadError}
              loading={
                <LoadingPage
                  height={estimatedPageHeight}
                  width={estimatedPageWidth}
                  fileIsLarge={fileIsLarge}
                />
              }
              error={<ErrorPage />}
              options={options}
              className="w-full h-full"
            >
              <div
                ref={containerRef}
                onScroll={handleScroll}
                {...readerPresence}
                // justify-center-safe theoretically ensures that the container is centered when not overflowing,
                // but then aligned to the start of the container when overflowing;
                // we must have a defined width (hence the firstVirtualItemPageWidth calculation) in order
                // for it to work properly
                className="flex justify-center-safe w-full h-full overflow-auto focus:outline-none"
              >
                <div
                  style={{
                    height: `${virtualizer.getTotalSize()}px`,
                    position: 'relative',
                    width: firstVirtualItemPageWidth,
                  }}
                  className={twMerge(
                    // without this, the scroll container is cut off at the sides when the page scale is greater than 1
                    pageScale > 1 && !centerOnZoom && 'justify-start',
                  )}
                >
                  {virtualItems.map((virtualItem) => {
                    const pageNumber = virtualItem.index + 1
                    const pageCitations =
                      citationsByPage.get(pageNumber) ?? EMPTY_CITATIONS
                    // Baked-box highlights can span pages; each page paints
                    // only its own slice.
                    const chipMatchesForPage =
                      chipHighlightMatches.length > 0
                        ? chipHighlightMatches.filter(
                            (match) => match.pageNumber === pageNumber,
                          )
                        : EMPTY_SEARCH_MATCHES
                    const pageSearchMatches = isFindOpen
                      ? (searchMatchesByPage.get(pageNumber) ??
                        EMPTY_SEARCH_MATCHES)
                      : chipMatchesForPage
                    const isOnScreen =
                      !!onScreenRange &&
                      virtualItem.index >= onScreenRange.startIndex &&
                      virtualItem.index <= onScreenRange.endIndex
                    return (
                      <div
                        data-index={virtualItem.index}
                        key={virtualItem.index}
                        ref={virtualizer.measureElement}
                        style={{
                          position: 'absolute',
                          top: 0,
                          transform: `translateY(${virtualItem.start}px)`,
                        }}
                      >
                        {!arePagesLive ? (
                          <PagePlaceholder
                            height={estimatedPageHeight}
                            width={estimatedPageWidth}
                          />
                        ) : pageRenderer ? (
                          pageRenderer({
                            index: virtualItem.index,
                            pageScale,
                            virtualizer,
                            isOnScreen,
                            isPageScaleSettling,
                            fileIsLarge,
                            citations: pageCitations,
                            estimatedPageWidth,
                            estimatedPageHeight,
                            activeCitationId,
                            activeCitationActivationKey,
                          })
                        ) : (
                          <MemoizedPage
                            key={virtualItem.index}
                            index={virtualItem.index}
                            pageScale={pageScale}
                            virtualizer={virtualizer}
                            isOnScreen={isOnScreen}
                            isPageScaleSettling={isPageScaleSettling}
                            citations={pageCitations}
                            estimatedPageWidth={estimatedPageWidth}
                            estimatedPageHeight={estimatedPageHeight}
                            activeCitationId={activeCitationId}
                            activeCitationActivationKey={
                              activeCitationActivationKey
                            }
                            onFirstPageRenderSuccess={
                              handleFirstPageRenderSuccess
                            }
                          />
                        )}
                        <PdfSearchHighlights
                          activeMatchId={
                            isFindOpen
                              ? activeSearchMatch?.pageNumber === pageNumber
                                ? activeSearchMatch.id
                                : undefined
                              : chipMatchesForPage[0]?.id
                          }
                          matches={pageSearchMatches}
                          pageScale={pageScale}
                          variant={isFindOpen ? 'search' : 'selection'}
                        />
                      </div>
                    )
                  })}
                </div>
              </div>
            </Document>
          </div>
        </>
      )
    },
  ),
)

interface PdfSearchHighlightsProps {
  activeMatchId?: string
  matches: PdfSearchMatch[]
  pageScale: number
  /**
   * `search`: find-bar matches — loud amber boxes, the active one louder,
   * built to be spotted while scanning. `selection`: a reopened selection
   * card's own highlight — the user already knows where it is, so it reads
   * like the native selection: the same selection blue the flowing-text
   * viewers paint for a restored selection (KEPT_SELECTION_STYLE,
   * `rgb(180 213 254)`), multiply-blended because these boxes overlay the
   * rendered page rather than sit behind the glyphs. The active data
   * attribute is visual-independent (the scroll glue queries it).
   */
  variant?: 'search' | 'selection'
}

export const PdfSearchHighlights: React.FC<PdfSearchHighlightsProps> = memo(
  ({ activeMatchId, matches, pageScale, variant = 'search' }) => {
    return matches.flatMap((match) => {
      const isActive = match.id === activeMatchId
      return match.bounds.map((bounds, boundsIndex) => (
        <div
          key={`${match.id}:${boundsIndex}`}
          className={twMerge(
            'pointer-events-none absolute',
            variant === 'selection'
              ? 'rounded-sm bg-[rgb(180_213_254)] mix-blend-multiply'
              : 'border-2 border-amber-500 bg-yellow-300/35 shadow-[0_0_0_1px_rgba(255,255,255,0.75)_inset]',
            variant === 'search' &&
              isActive &&
              'border-orange-600 bg-orange-300/40 shadow-[0_0_0_2px_rgba(234,88,12,0.2)]',
          )}
          style={{
            height: bounds.height * pageScale,
            left: bounds.left * pageScale,
            top: bounds.top * pageScale,
            width: bounds.width * pageScale,
          }}
          data-pdf-search-highlight={isActive ? 'active' : 'match'}
          data-pdf-search-match-id={match.id}
          aria-hidden
        />
      ))
    })
  },
)

PdfSearchHighlights.displayName = 'PdfViewer.V2.SearchHighlights'

interface PageProps {
  index: number
  pageScale: number
  virtualizer: Virtualizer<HTMLDivElement, Element>
  /** Within the viewport rather than rendered ahead of it. */
  isOnScreen: boolean
  /** The scale is on its way to a new value and the page will be redrawn. */
  isPageScaleSettling: boolean
  citations?: Citation[]
  estimatedPageWidth: number
  estimatedPageHeight: number
  activeCitationId?: number
  activeCitationActivationKey?: number
  onFirstPageRenderSuccess?: () => void
}

const Page: React.FC<PageProps> = ({
  index,
  pageScale,
  virtualizer,
  isOnScreen,
  isPageScaleSettling,
  citations,
  estimatedPageWidth,
  estimatedPageHeight,
  activeCitationId,
  activeCitationActivationKey,
  onFirstPageRenderSuccess,
}) => {
  const pageNumber = index + 1
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLDivElement>(null)

  const pageDimensions = usePageDimensions(pageNumber)
  const setPageDimensions = useSetPageDimensions(pageNumber)

  const pageHeight = usePageHeight(pageNumber)
  const setPageHeight = useSetPageHeight(pageNumber)

  const loadingWidth = pageDimensions
    ? inchesToPixels({
        inches: pageDimensions.widthInches,
        pageScale,
      })
    : estimatedPageWidth
  const renderedPageHeight = pageDimensions
    ? inchesToPixels({
        inches: pageDimensions.heightInches,
        pageScale,
      })
    : (pageHeight ?? estimatedPageHeight)

  const handleOnLoadSuccess = useCallback(
    async (page: pdfjs.PDFPageProxy) => {
      setIsPageLoaded(true)
      const viewport = page.getViewport({ scale: pageScale })
      const pageHeight = viewport.height
      if (pageHeight) {
        setPageHeight(pageHeight)
      }

      // Get page dimensions in inches (PDF units are in points, 72 points = 1 inch)
      const originalViewport = page.getViewport({ scale: 1 })
      const widthInches = originalViewport.width / 72
      const heightInches = originalViewport.height / 72

      setPageDimensions({
        widthInches,
        heightInches,
      })
    },
    [pageScale, setPageDimensions, setPageHeight],
  )

  // The settled scale is the build key: react-pdf discards work done at one
  // scale at the next, so a grant is only as good as the scale it was made at.
  const shouldRenderCanvas = useIdleOverscanCanvas(isOnScreen, pageScale)
  const [isPageLoaded, setIsPageLoaded] = useState(false)
  // A canvas mounts transparent and stays that way until pdf.js paints into it,
  // and a scale change clears it back to transparent for the repaint. Tracked
  // by scale rather than reset in an effect, so the frame that clears the
  // canvas is already the frame that covers it.
  const [paintedPageScale, setPaintedPageScale] = useState<number | null>(null)
  const isCanvasRendered = shouldRenderCanvas && paintedPageScale === pageScale
  const isTextLayerQueued = useIdlePageLayers(
    isCanvasRendered && isOnScreen,
    pageScale,
  )
  // Layers mounted at a scale that is still moving are about to be rebuilt, and
  // every span costs a walk on each frame of the drag until then.
  const isTextLayerReady = isTextLayerQueued && !isPageScaleSettling
  // Not deferred: the annotation layer is what makes a link a link, and a
  // reader who clicks one the moment the page appears would hit bare canvas.
  // It is also cheap — the text layer is what measures every span it draws.
  const isAnnotationLayerReady = isCanvasRendered && isOnScreen

  const handleRenderSuccess = useCallback(() => {
    setPaintedPageScale(pageScale)
    if (pageNumber === 1) {
      onFirstPageRenderSuccess?.()
    }
  }, [onFirstPageRenderSuccess, pageNumber, pageScale])

  useEffect(() => {
    // https://github.com/TanStack/virtual/issues/528
    virtualizer.measureElement(containerRef.current)
  }, [pageScale, virtualizer])

  return (
    <div
      ref={containerRef}
      className="pdf-viewer-page group flex relative cursor-default"
      data-index={index}
      data-page-number={pageNumber}
      style={{
        height: `${renderedPageHeight}px`,
        boxShadow: '0 0 3px rgba(0, 0, 0, 0.5)',
        willChange: 'width',
        minWidth: '200px',
      }}
    >
      <ReactPdfPage
        inputRef={inputRef}
        key={`page_${pageNumber}`}
        pageNumber={pageNumber}
        scale={pageScale}
        renderMode={shouldRenderCanvas ? 'canvas' : 'none'}
        renderTextLayer={isTextLayerReady}
        renderAnnotationLayer={isAnnotationLayerReady}
        onLoadSuccess={handleOnLoadSuccess}
        onRenderSuccess={handleRenderSuccess}
        loading={
          <PagePlaceholder height={renderedPageHeight} width={loadingWidth} />
        }
      />
      {/* At renderMode 'none' a loaded page renders nothing at all, and with
          no canvas the box collapses to its min-width. */}
      {isPageLoaded && !shouldRenderCanvas && (
        <PagePlaceholder height={renderedPageHeight} width={loadingWidth} />
      )}
      {/* A granted canvas is transparent until its paint finishes — cover it so
          the reader sees the white box, not the panel through the page. */}
      {shouldRenderCanvas && !isCanvasRendered && (
        <div className="absolute inset-0 bg-white pointer-events-none" />
      )}
      {citations && pageDimensions && (
        <CitationOverlay
          citations={citations}
          pageDimensions={pageDimensions}
          pageScale={pageScale}
          pageNumber={pageNumber}
          activeCitationId={activeCitationId}
          activeCitationActivationKey={activeCitationActivationKey}
        />
      )}
    </div>
  )
}

const MemoizedPage = memo(Page)

interface ConvertInchesToPixelsProps {
  point: Point
  pageDimensions: PageDimensions
  pageScale: number
}

export const inchesToPixels = ({
  inches,
  pageScale,
}: {
  inches: number
  pageScale: number
}) => {
  return inches * 72 * pageScale
}

const convertInchesToPixels = ({
  point,
  pageDimensions,
  pageScale,
}: ConvertInchesToPixelsProps): Point | null => {
  const [xInches, yInches] = point

  // Calculate the actual rendered width based on the page scale
  const renderedWidth = pageDimensions.widthInches * 72 * pageScale
  const renderedHeight = pageDimensions.heightInches * 72 * pageScale

  // Convert inches to pixels
  const xPixels = (xInches / pageDimensions.widthInches) * renderedWidth
  const yPixels = (yInches / pageDimensions.heightInches) * renderedHeight

  return [xPixels, yPixels]
}

interface CitationOverlayProps {
  citations: Citation[]
  pageDimensions: PageDimensions
  pageScale: number
  pageNumber: number
  activeCitationId?: number
  activeCitationActivationKey?: number
}

const CitationOverlay: React.FC<CitationOverlayProps> = memo(
  ({
    citations,
    pageDimensions,
    pageScale,
    pageNumber,
    activeCitationId,
    activeCitationActivationKey,
  }) => {
    return (
      <>
        {citations.map((citation, index) => {
          return (
            <CitationBox
              key={`citation-${pageNumber}-${index}`}
              citation={citation}
              pageDimensions={pageDimensions}
              pageScale={pageScale}
              activeCitationId={activeCitationId}
              activeCitationActivationKey={activeCitationActivationKey}
            />
          )
        })}
      </>
    )
  },
)

interface CitationBoxProps {
  citation: Citation
  pageDimensions: PageDimensions
  pageScale: number
  activeCitationId?: number
  activeCitationActivationKey?: number
}

const CITATION_GREEN = 'rgb(34, 197, 94)' // green-500
const CITATION_GREEN_FILL = 'rgba(34, 197, 94, 0.09)'
const CITATION_GREEN_ACTIVE_FILL = 'rgba(34, 197, 94, 0.2)'
const CITATION_GREEN_HALO = 'rgba(34, 197, 94, 0.16)'
const CITATION_HIGHLIGHT_PULSE_MS = 900

const CitationBox: React.FC<CitationBoxProps> = memo(
  ({
    citation,
    pageDimensions,
    pageScale,
    activeCitationId,
    activeCitationActivationKey,
  }) => {
    const { bounding_box, color: colorHex } = citation
    const [isPulsing, setIsPulsing] = useState(false)
    const isActive =
      activeCitationId !== undefined && citation.id === activeCitationId

    const borderColor = colorHex ? toRgba(colorHex, 1) : CITATION_GREEN
    const fillColor = colorHex ? toRgba(colorHex, 0.08) : CITATION_GREEN_FILL
    const activeFillColor = colorHex
      ? toRgba(colorHex, 0.18)
      : CITATION_GREEN_ACTIVE_FILL
    const haloColor = colorHex ? toRgba(colorHex, 0.14) : CITATION_GREEN_HALO

    useEffect(() => {
      if (!isActive) {
        setIsPulsing(false)
        return
      }

      setIsPulsing(true)
      const timeoutId = window.setTimeout(() => {
        setIsPulsing(false)
      }, CITATION_HIGHLIGHT_PULSE_MS)

      return () => {
        window.clearTimeout(timeoutId)
      }
    }, [activeCitationActivationKey, isActive])

    if (!bounding_box) return null

    const convertedPoints = bounding_box.map((point) =>
      convertInchesToPixels({ point, pageDimensions, pageScale }),
    )

    if (convertedPoints.some((p) => p === null)) return null

    // Get min/max coordinates to create a rectangle
    const xCoords = convertedPoints.map((p) => p![0])
    const yCoords = convertedPoints.map((p) => p![1])
    const minX = Math.min(...xCoords)
    const maxX = Math.max(...xCoords)
    const minY = Math.min(...yCoords)
    const maxY = Math.max(...yCoords)

    const width = maxX - minX
    const height = maxY - minY
    const boxShadow = isPulsing
      ? `0 0 0 1px rgba(255, 255, 255, 0.85) inset, 0 0 0 5px ${haloColor}, 0 0 24px ${haloColor}`
      : `0 0 0 1px rgba(255, 255, 255, 0.7) inset, 0 0 0 3px ${haloColor}`

    return (
      <div
        data-citation-id={citation.id}
        className="pointer-events-none absolute"
        style={{
          left: `${minX - 4}px`,
          top: `${minY - 2}px`,
          width: `${width + 8}px`,
          height: `${height + 4}px`,
          backgroundColor: isPulsing ? activeFillColor : fillColor,
          border: `1.25px solid ${borderColor}`,
          borderRadius: '5px',
          boxShadow,
          transform: isPulsing ? 'scale(1.025)' : undefined,
          transformOrigin: 'center',
          transition:
            'transform 900ms cubic-bezier(0.2, 0, 0, 1), box-shadow 900ms cubic-bezier(0.2, 0, 0, 1), background-color 150ms ease',
        }}
      />
    )
  },
)

CitationOverlay.displayName = 'PdfViewer.V2.CitationOverlay'
CitationBox.displayName = 'PdfViewer.V2.CitationBox'

PdfViewer.displayName = 'PdfViewer.V2'
